import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  ChevronDownIcon,
  ChevronRightIcon,
  CircleHelpIcon,
  ExternalLinkIcon,
  SearchIcon,
  TriangleAlertIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import useMcpServers, {
  type McpServer,
  type McpTool,
} from '@/components/pages/MCPs/hooks/useMcpServers';
import { getAuthHeaders } from '@/components/pages/MCPs/ResyncDialog';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import useBrains from '@/hooks/useBrains';
import { useEffectiveTools } from '@/hooks/useEffectiveTools';
import { cn } from '@/lib/utils';
import { selectAvailableLocalTools } from '@/redux/featureFlag/selectors';
import { selectAccountSlug, selectBrainId } from '@/redux/session/selectors';
import { Tool } from '@/shared/types/brain';

type LocalToolCategory = 'generic' | 'voice_channels';

type LocalToolItem = { tool: Tool; labelKey: string; category: LocalToolCategory };

const LOCAL_TOOLS: LocalToolItem[] = [
  { tool: Tool.TRIGGER_WORKFLOW, labelKey: 'local_tools.trigger_workflow', category: 'generic' },
  {
    tool: Tool.COMPOSE_RICH_RESPONSE,
    labelKey: 'local_tools.compose_rich_response',
    category: 'generic',
  },
  { tool: Tool.EXECUTE_CODE, labelKey: 'local_tools.execute_code', category: 'generic' },
  { tool: Tool.ASK_QUESTIONS, labelKey: 'local_tools.ask_questions', category: 'generic' },
  {
    tool: Tool.VOICEMAIL_DETECTION,
    labelKey: 'local_tools.voicemail_detection',
    category: 'voice_channels',
  },
];

/** Enabled tools of a server — a server toggled off (is_active=false) contributes none */
export const getEnabledTools = (server: McpServer): McpTool[] => {
  return server.is_active ? server.tools : [];
};

export const isToolEnabled = (server: McpServer, toolName: string): boolean => {
  return getEnabledTools(server).some((t) => t.name === toolName);
};

type ToolsSelectorProps = { helpTooltipSide?: 'top' | 'right' | 'bottom' | 'left' };

export const ToolsSelector = ({ helpTooltipSide = 'right' }: ToolsSelectorProps = {}) => {
  const { t } = useTranslation();
  const brainId = useSelector(selectBrainId);
  const slug = useSelector(selectAccountSlug);
  const { brain, updateBrain } = useBrains(brainId);
  const { mcpServers, updateMcpServer, testMutation } = useMcpServers(brainId ?? undefined);

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [expandedServers, setExpandedServers] = useState<Set<string>>(new Set());

  /** Prefetched full tool lists per server — stored in a ref to avoid re-render flicker */
  const allToolsMapRef = useRef<Record<string, McpTool[]>>({});

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    // Radix focuses the first menu item on open; wait a tick so our focus wins.
    const id = window.setTimeout(() => searchInputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [isOpen]);

  type FetchState = { status: 'idle' | 'loading' | 'loaded' | 'failed'; error?: string };
  const [serverFetchState, setServerFetchState] = useState<Record<string, FetchState>>({});

  const { effectiveTools: enabledLocalTools } = useEffectiveTools(brain);
  const availableTools = useSelector(selectAvailableLocalTools);

  /** Get all tools for a server: prefetched list (ref) > enabled tools (server state) */
  const getServerAllTools = useCallback((server: McpServer): McpTool[] => {
    return allToolsMapRef.current[server.mcp_id] ?? server.tools;
  }, []);

  /** Fetch full tools list via /mcp/test for a server (writes to ref, no re-render) */
  const fetchServerTools = useCallback(
    async (server: McpServer) => {
      const state = serverFetchState[server.mcp_id]?.status;
      if (state === 'loading' || state === 'loaded') {
        return;
      }
      setServerFetchState((prev) => ({ ...prev, [server.mcp_id]: { status: 'loading' } }));
      const headers = getAuthHeaders(server);
      try {
        const result = await testMutation.mutateAsync({ url: server.url, headers });
        setServerFetchState((prev) => ({ ...prev, [server.mcp_id]: { status: 'loaded' } }));
        if (result.tools) {
          allToolsMapRef.current[server.mcp_id] = result.tools as McpTool[];
        }
      } catch (err) {
        const error = err instanceof Error ? err.message : undefined;
        setServerFetchState((prev) => ({ ...prev, [server.mcp_id]: { status: 'failed', error } }));
      }
    },
    [serverFetchState, testMutation]
  );

  const prefetchAllServers = useCallback(() => {
    mcpServers.forEach((server) => {
      fetchServerTools(server);
    });
  }, [mcpServers, fetchServerTools]);

  const toggleExpanded = useCallback((serverId: string) => {
    setExpandedServers((prev) => {
      const next = new Set(prev);
      if (next.has(serverId)) {
        next.delete(serverId);
      } else {
        next.add(serverId);
      }
      return next;
    });
  }, []);

  const getServerCheckState = useCallback(
    (server: McpServer): boolean | 'indeterminate' => {
      const allTools = getServerAllTools(server);
      const enabledCount = getEnabledTools(server).length;
      if (enabledCount === 0) {
        return false;
      }
      if (allTools.length > 0 && enabledCount < allTools.length) {
        return 'indeterminate';
      }
      return true;
    },
    [getServerAllTools]
  );

  const handleToggleLocalTool = useCallback(
    (tool: Tool, checked: boolean) => {
      if (!brain) {
        return;
      }
      const currentTools = brain.tools ?? [];
      const nextTools = checked ? [...currentTools, tool] : currentTools.filter((t) => t !== tool);
      updateBrain({ brain_id: brain.brain_id, tools: nextTools, preventToast: true });
    },
    [brain, updateBrain]
  );

  /** Toggle an individual MCP tool — sends updated tools array to backend */
  const handleToggleMcpTool = useCallback(
    (server: McpServer, mcpTool: McpTool, checked: boolean) => {
      // Snapshot tool order in ref so toggling doesn't reorder the list
      if (!allToolsMapRef.current[server.mcp_id]) {
        allToolsMapRef.current[server.mcp_id] = getServerAllTools(server);
      }

      const enabledTools = getEnabledTools(server);
      const nextEnabled = checked
        ? [...enabledTools, mcpTool]
        : enabledTools.filter((t) => t.name !== mcpTool.name);
      const nextIsActive = nextEnabled.length > 0;
      updateMcpServer({
        brain_id: server.brain_id,
        mcp_id: server.mcp_id,
        tools: nextEnabled,
        ...(nextIsActive !== server.is_active && { is_active: nextIsActive }),
      });
    },
    [getServerAllTools, updateMcpServer]
  );

  /** Toggle entire MCP server — checks/unchecks all tools */
  const handleToggleMcpServer = useCallback(
    (server: McpServer) => {
      const checkState = getServerCheckState(server);
      const allTools = getServerAllTools(server);

      // checked → uncheck all tools
      if (checkState === true) {
        // Snapshot tools before clearing so re-checking can restore them
        if (!allToolsMapRef.current[server.mcp_id] && allTools.length > 0) {
          allToolsMapRef.current[server.mcp_id] = allTools;
        }
        updateMcpServer({
          brain_id: server.brain_id,
          mcp_id: server.mcp_id,
          is_active: false,
          tools: [],
        });
        return;
      }

      // unchecked or indeterminate → enable all tools
      updateMcpServer({
        brain_id: server.brain_id,
        mcp_id: server.mcp_id,
        is_active: true,
        tools: allTools,
      });
    },
    [getServerAllTools, getServerCheckState, updateMcpServer]
  );

  const searchLower = search.toLowerCase();

  const isSearching = searchLower.length > 0;

  // Filter MCP servers: match by server name OR by any tool name (enabled or disabled)
  const filteredMcpServers = useMemo(
    () =>
      mcpServers.filter((server) => {
        if (!searchLower) {
          return true;
        }
        if (server.name.toLowerCase().includes(searchLower)) {
          return true;
        }
        return getServerAllTools(server).some((tool) =>
          tool.name.toLowerCase().includes(searchLower)
        );
      }),
    [getServerAllTools, mcpServers, searchLower]
  );

  // When searching, flatten MCP tools that match grouped by server
  const mcpSearchResults = useMemo(() => {
    if (!isSearching) {
      return [];
    }
    return filteredMcpServers
      .map((server) => {
        const allTools = getServerAllTools(server);
        const serverNameMatches = server.name.toLowerCase().includes(searchLower);
        const matchingTools = serverNameMatches
          ? allTools
          : allTools.filter((tool) => tool.name.toLowerCase().includes(searchLower));
        return { server, tools: matchingTools };
      })
      .filter((entry) => entry.tools.length > 0);
  }, [getServerAllTools, isSearching, filteredMcpServers, searchLower]);

  const filteredLocalTools = useMemo(
    () =>
      LOCAL_TOOLS.filter((item) => {
        if (!availableTools.includes(item.tool)) {
          return false;
        }
        if (!searchLower) {
          return true;
        }
        return t(item.labelKey).toLowerCase().includes(searchLower);
      }),
    [availableTools, searchLower, t]
  );

  const genericTools = filteredLocalTools.filter((item) => item.category === 'generic');
  const voiceTools = filteredLocalTools.filter((item) => item.category === 'voice_channels');

  const enabledCount = useMemo(() => {
    const activeMcpToolCount = mcpServers.reduce(
      (acc, server) => acc + getEnabledTools(server).length,
      0
    );
    return activeMcpToolCount + enabledLocalTools.length;
  }, [mcpServers, enabledLocalTools]);

  const hasMcpResults = isSearching ? mcpSearchResults.length > 0 : filteredMcpServers.length > 0;
  const hasResults = hasMcpResults || genericTools.length > 0 || voiceTools.length > 0;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1 text-xs leading-4 font-medium text-muted-foreground">
        {t('ai_agents.guidelines.tools')}
        <InfoTooltip content={t('ai_agents.guidelines.tools_tooltip')} side={helpTooltipSide}>
          <CircleHelpIcon size={12} className="text-muted-foreground" />
        </InfoTooltip>
      </div>

      <DropdownMenu
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);
          if (open) {
            prefetchAllServers();
          } else {
            setSearch('');
            setExpandedServers(new Set());
          }
        }}
      >
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t('ai_agents.guidelines.tools')}
            className="flex h-auto items-center gap-1 text-sm leading-6 font-medium text-foreground"
          >
            <span>{enabledCount}</span>
            <ChevronDownIcon className="size-4 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent side="left" align="start" className="w-64 p-0">
          <div className="flex h-9 items-center gap-2 border-b px-3">
            <SearchIcon className="size-4 shrink-0 text-muted-foreground" />
            <Input
              ref={searchInputRef}
              placeholder={t('ai_agents.guidelines.search_tools')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              className="h-9 rounded-none border-0 bg-transparent px-0 py-3 text-sm shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </div>

          <div className="max-h-64 overflow-y-auto p-1">
            {!hasResults && (
              <p className="py-4 text-center text-sm text-muted-foreground">
                {t('common.no_results')}
              </p>
            )}

            {/* MCP servers: inline collapsible when not searching, flat view when searching */}
            {!isSearching && filteredMcpServers.length > 0 && (
              <DropdownMenuGroup>
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  {t('ai_agents.guidelines.tools_mcp')}
                </DropdownMenuLabel>
                {filteredMcpServers.map((server) => {
                  const isExpanded = expandedServers.has(server.mcp_id);
                  const allTools = getServerAllTools(server);
                  const checkState = getServerCheckState(server);
                  const fetchState = serverFetchState[server.mcp_id];
                  const isFetchFailed = fetchState?.status === 'failed';
                  const isFetchLoading = fetchState?.status === 'loading';

                  return (
                    <div key={server.mcp_id}>
                      <DropdownMenuItem
                        className="gap-2"
                        onSelect={(e) => {
                          e.preventDefault();
                          toggleExpanded(server.mcp_id);
                        }}
                      >
                        <Checkbox
                          className="[&_svg]:text-current!"
                          checked={checkState}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleToggleMcpServer(server);
                          }}
                        />
                        <span className="flex-1 truncate">{server.name}</span>
                        {isFetchLoading && <Spinner className="shrink-0" />}
                        {isFetchFailed && (
                          <InfoTooltip
                            content={fetchState?.error || t('mcp.tools_status_unreachable')}
                            size="auto"
                            side="top"
                          >
                            <span>
                              <TriangleAlertIcon className="size-3.5 shrink-0 text-warning" />
                            </span>
                          </InfoTooltip>
                        )}
                        <ChevronRightIcon
                          className={cn(
                            'size-4 text-muted-foreground transition-transform',
                            isExpanded && 'rotate-90'
                          )}
                        />
                      </DropdownMenuItem>

                      {isExpanded && (
                        <div className="pl-4">
                          {isFetchFailed && (
                            <DropdownMenuItem
                              className="gap-1.5 text-xs text-muted-foreground"
                              onSelect={(e) => {
                                e.preventDefault();
                                setServerFetchState((prev) => ({
                                  ...prev,
                                  [server.mcp_id]: { status: 'idle' },
                                }));
                                fetchServerTools(server);
                              }}
                            >
                              <TriangleAlertIcon className="size-3 shrink-0 text-warning" />
                              <span>{t('mcp.tools_status_unreachable')}</span>
                              <span className="ml-auto shrink-0 text-primary">
                                {t('common.try_again')}
                              </span>
                            </DropdownMenuItem>
                          )}
                          {allTools.map((tool) => (
                            <DropdownMenuItem
                              key={tool.name}
                              className="gap-2"
                              onSelect={(e) => {
                                e.preventDefault();
                                handleToggleMcpTool(
                                  server,
                                  tool,
                                  !isToolEnabled(server, tool.name)
                                );
                              }}
                            >
                              <Checkbox
                                className="pointer-events-none [&_svg]:text-current!"
                                checked={isToolEnabled(server, tool.name)}
                              />
                              <span className="flex-1 truncate text-sm">{tool.name}</span>
                            </DropdownMenuItem>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </DropdownMenuGroup>
            )}

            {isSearching &&
              mcpSearchResults.map(({ server, tools }) => (
                <DropdownMenuGroup key={server.mcp_id}>
                  <DropdownMenuLabel className="text-xs text-muted-foreground">
                    {server.name}
                  </DropdownMenuLabel>
                  {tools.map((tool) => (
                    <DropdownMenuItem
                      key={tool.name}
                      className="gap-2"
                      onSelect={(e) => {
                        e.preventDefault();
                        handleToggleMcpTool(server, tool, !isToolEnabled(server, tool.name));
                      }}
                    >
                      <Checkbox
                        className="pointer-events-none [&_svg]:text-current!"
                        checked={isToolEnabled(server, tool.name)}
                      />
                      <span className="flex-1 truncate text-sm">{tool.name}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              ))}

            {genericTools.length > 0 && (
              <DropdownMenuGroup>
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  {t('ai_agents.guidelines.tools_generic')}
                </DropdownMenuLabel>
                {genericTools.map((item) => (
                  <DropdownMenuItem
                    key={item.tool}
                    onSelect={(e) => {
                      e.preventDefault();
                      handleToggleLocalTool(item.tool, !enabledLocalTools.includes(item.tool));
                    }}
                    className="group gap-2"
                  >
                    <Checkbox
                      className="pointer-events-none [&_svg]:text-current!"
                      checked={enabledLocalTools.includes(item.tool)}
                    />
                    <span className="flex-1 truncate">{t(item.labelKey)}</span>
                    <a
                      href="https://docs.moveo.ai/docs/ai-agents/local-tools"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="opacity-0 group-hover:opacity-100"
                    >
                      <ExternalLinkIcon className="size-3.5 text-muted-foreground hover:text-foreground" />
                    </a>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
            )}

            {voiceTools.length > 0 && (
              <DropdownMenuGroup>
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  {t('ai_agents.guidelines.tools_voice_channels')}
                </DropdownMenuLabel>
                {voiceTools.map((item) => (
                  <DropdownMenuItem
                    key={item.tool}
                    onSelect={(e) => {
                      e.preventDefault();
                      handleToggleLocalTool(item.tool, !enabledLocalTools.includes(item.tool));
                    }}
                    className="group gap-2"
                  >
                    <Checkbox
                      className="pointer-events-none [&_svg]:text-current!"
                      checked={enabledLocalTools.includes(item.tool)}
                    />
                    <span className="flex-1 truncate">{t(item.labelKey)}</span>
                    <a
                      href="https://docs.moveo.ai/docs/ai-agents/local-tools"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="opacity-0 group-hover:opacity-100"
                    >
                      <ExternalLinkIcon className="size-3.5 text-muted-foreground hover:text-foreground" />
                    </a>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
            )}
          </div>

          <div className="border-t p-1">
            <DropdownMenuItem asChild>
              <Link
                to={`/${slug}/ai-agents/${brainId}/mcps`}
                className="flex items-center justify-center gap-1 text-sm font-medium"
              >
                {t('ai_agents.guidelines.go_to_tools')}
              </Link>
            </DropdownMenuItem>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
